import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Descriptions, Space, Typography, Button, Spin, Alert, Tag } from 'antd';
import { ArrowUpOutlined } from '@ant-design/icons';
import tenantService from '../../services/tenantService';
import logger from '../../utils/logger';
import { getApiErrorMessage } from '../../utils/apiError';

const { Title, Text } = Typography;

/**
 * TenantDetailsPage — super-admin view of one tenant (FE-5-9)
 * Location: frontend/src/components/admin/TenantDetailsPage.js
 *
 * Shows the tenant's real record from GET /admin/tenants/{id}/details. Any
 * field may be null (BE-5-23), so every value renders through `display()`.
 *
 * Resources, cost and metrics are deliberately not shown: resource discovery is
 * an unfinished stub in production (REQ-004), and this page used to render
 * hard-coded sample data in their place. Tier changes stay disabled for the
 * same reason, and the compute panel (which carried a live RDS upgrade action)
 * is not rendered here.
 */

const EMPTY = '—';

const display = (value) => (value === null || value === undefined || value === '' ? EMPTY : value);

const formatDate = (value) => {
  if (!value) return EMPTY;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? EMPTY : date.toLocaleString();
};

const STATUS_COLORS = {
  READY: 'green',
  ACTIVE: 'green',
  PROVISIONING: 'blue',
  FAILED: 'red',
  SUSPENDED: 'orange',
};

const TenantDetailsPage = () => {
  const { tenantId } = useParams();
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [error, setError] = useState(null);

  const fetchTenant = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await tenantService.getAdminTenantDetails(tenantId);
      setTenant(data);
    } catch (err) {
      logger.error('Error fetching tenant details:', err);
      setTenant(null);
      setError(err?.response?.status === 404
        ? 'Tenant not found.'
        : getApiErrorMessage(err, 'Could not load this tenant.'));
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" aria-label="Loading tenant" />
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          showIcon
          message={error || 'Tenant not found.'}
          action={<Button size="small" onClick={fetchTenant}>Retry</Button>}
        />
      </div>
    );
  }

  const status = tenant.status ? String(tenant.status) : null;

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <Title level={2} style={{ margin: 0 }}>
                Tenant Details: {display(tenant.name || tenant.tenant_id)}
              </Title>
              <Space direction="vertical" size={2} align="end">
                <Button type="primary" icon={<ArrowUpOutlined />} disabled>
                  Upgrade Tier
                </Button>
                <Text type="secondary" style={{ fontSize: 12 }}>Tier changes are not yet available.</Text>
              </Space>
            </div>

            <Descriptions column={{ xs: 1, sm: 2, lg: 3 }} bordered size="small">
              <Descriptions.Item label="Tenant ID"><code>{display(tenant.tenant_id)}</code></Descriptions.Item>
              <Descriptions.Item label="Name">{display(tenant.name)}</Descriptions.Item>
              <Descriptions.Item label="Tier">
                {tenant.tier ? <Tag>{tenant.tier}</Tag> : EMPTY}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {status ? <Tag color={STATUS_COLORS[status.toUpperCase()]}>{status}</Tag> : EMPTY}
              </Descriptions.Item>
              <Descriptions.Item label="Contact Email">{display(tenant.email)}</Descriptions.Item>
              <Descriptions.Item label="Created By">{display(tenant.created_by)}</Descriptions.Item>
              <Descriptions.Item label="Created">{formatDate(tenant.created_at)}</Descriptions.Item>
              <Descriptions.Item label="Last Updated">{formatDate(tenant.updated_at)}</Descriptions.Item>
            </Descriptions>
          </Space>
        </Card>

        <Card title="Resources">
          <Alert
            type="info"
            showIcon
            message="Resource details are not yet available"
            description="Encryption keys, storage, database, cost and usage metrics for this tenant can't be shown yet. Resource discovery isn't available, so nothing here is estimated or sample data."
          />
        </Card>
      </Space>
    </div>
  );
};

export default TenantDetailsPage;
